import React, { useState } from 'react';
import { Form, Button, Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { adminClientAPI } from '../api/api';
import './ClientSetup.css';

/**
 * Client Setup Form Component
 * Allows administrators to create and configure new clients
 */
const ClientSetup = ({ onClientCreated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    companyCode: '',
    industry: '',
    description: '',

    // Primary Contact
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',

    // Company Address
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',

    // Billing Contact
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: '',

    // Support Contact
    supportContactName: '',
    supportContactEmail: '',
    supportContactPhone: '',

    // Logo
    companyLogoFile: null
  });

  const [activeTab, setActiveTab] = useState('company');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        companyLogoFile: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!formData.companyCode.trim()) {
      setError('Company code is required');
      return false;
    }
    if (!formData.contactName.trim()) {
      setError('Contact name is required');
      return false;
    }
    if (!formData.contactEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      setError('Valid contact email is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data - convert file to base64 if it exists
      let submitData = { ...formData };
      
      // Handle logo file upload separately if needed
      if (formData.companyLogoFile) {
        // For now, we'll skip file upload - just send basic data
        delete submitData.companyLogoFile;
      }

      const response = await adminClientAPI.createClient(submitData);

      if (response.data && response.data.success) {
        setSuccess(`Client "${formData.companyName}" created successfully!`);
        
        // Reset form
        setFormData({
          companyName: '',
          companyCode: '',
          industry: '',
          description: '',
          contactName: '',
          contactTitle: '',
          contactEmail: '',
          contactPhone: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          country: 'USA',
          billingContactName: '',
          billingContactEmail: '',
          billingContactPhone: '',
          supportContactName: '',
          supportContactEmail: '',
          supportContactPhone: '',
          companyLogoFile: null
        });
        setLogoPreview(null);

        // Call callback if provided
        if (onClientCreated) {
          onClientCreated(response.data.data);
        }
      } else {
        throw new Error(response.data?.message || 'Failed to create client');
      }

      // Scroll to top
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tabContent = {
    company: (
      <>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Company Name *</Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Enter company name"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Company Code *</Form.Label>
              <Form.Control
                type="text"
                name="companyCode"
                value={formData.companyCode}
                onChange={handleInputChange}
                placeholder="e.g., ACME-001"
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Industry</Form.Label>
              <Form.Control
                type="text"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                placeholder="e.g., Technology, Retail"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Company Logo</Form.Label>
              <Form.Control
                type="file"
                name="logoFile"
                onChange={handleLogoChange}
                accept="image/*"
              />
              {logoPreview && (
                <div className="mt-3">
                  <img src={logoPreview} alt="Logo Preview" className="logo-preview" />
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Company description and notes"
          />
        </Form.Group>
      </>
    ),

    contact: (
      <>
        <h6 className="mb-3">Primary Contact</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Contact Name *</Form.Label>
              <Form.Control
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={handleInputChange}
                placeholder="Full name"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Title/Position</Form.Label>
              <Form.Control
                type="text"
                name="contactTitle"
                value={formData.contactTitle}
                onChange={handleInputChange}
                placeholder="e.g., Manager, Director"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                placeholder="email@example.com"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
            </Form.Group>
          </Col>
        </Row>

        <hr className="my-4" />
        <h6 className="mb-3">Billing Contact (Optional)</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="billingContactName"
                value={formData.billingContactName}
                onChange={handleInputChange}
                placeholder="Full name"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="billingContactEmail"
                value={formData.billingContactEmail}
                onChange={handleInputChange}
                placeholder="billing@example.com"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            name="billingContactPhone"
            value={formData.billingContactPhone}
            onChange={handleInputChange}
            placeholder="(555) 123-4567"
          />
        </Form.Group>

        <hr className="my-4" />
        <h6 className="mb-3">Support Contact (Optional)</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="supportContactName"
                value={formData.supportContactName}
                onChange={handleInputChange}
                placeholder="Full name"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="supportContactEmail"
                value={formData.supportContactEmail}
                onChange={handleInputChange}
                placeholder="support@example.com"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            name="supportContactPhone"
            value={formData.supportContactPhone}
            onChange={handleInputChange}
            placeholder="(555) 123-4567"
          />
        </Form.Group>
      </>
    ),

    address: (
      <>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 1</Form.Label>
              <Form.Control
                type="text"
                name="address1"
                value={formData.address1}
                onChange={handleInputChange}
                placeholder="Street address"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                name="address2"
                value={formData.address2}
                onChange={handleInputChange}
                placeholder="Suite, apartment, etc."
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="City"
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="State/Province"
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                placeholder="Zip code"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Country"
              />
            </Form.Group>
          </Col>
        </Row>
      </>
    )
  };

  return (
    <Container className="client-setup py-4">
      <Card>
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">Create New Client</h4>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

          <div className="tabs-nav mb-4">
            <button
              className={`tab-button ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              Company Info
            </button>
            <button
              className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              Contacts
            </button>
            <button
              className={`tab-button ${activeTab === 'address' ? 'active' : ''}`}
              onClick={() => setActiveTab('address')}
            >
              Address
            </button>
          </div>

          <Form onSubmit={handleSubmit}>
            {tabContent[activeTab]}

            <div className="mt-5 d-flex gap-2 justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  if (activeTab === 'company') setActiveTab('company');
                  else if (activeTab === 'contact') setActiveTab('company');
                  else if (activeTab === 'address') setActiveTab('contact');
                }}
                disabled={activeTab === 'company'}
              >
                ← Previous
              </Button>

              <Button
                variant="outline-primary"
                onClick={() => {
                  if (activeTab === 'company') setActiveTab('contact');
                  else if (activeTab === 'contact') setActiveTab('address');
                }}
                disabled={activeTab === 'address'}
              >
                Next →
              </Button>

              <Button
                variant="success"
                type="submit"
                disabled={loading}
                className="px-5"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Creating Client...
                  </>
                ) : (
                  'Create Client'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ClientSetup;
